const STORAGE_KEY = 'safeguardpro_roles_v1';

const MODULES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'students', label: 'Students' },
  { id: 'safeguarding', label: 'Safeguarding' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'programmes', label: 'Activities & Programmes' },
  { id: 'messages', label: 'Internal Messages' },
  { id: 'external-messages', label: 'External Messages' },
  { id: 'reports', label: 'Reports' },
  { id: 'support-workers', label: 'Support Workers' },
  { id: 'broadcasts', label: 'Announcements' },
  { id: 'settings', label: 'Settings' }
];

const buildAllAccess = () =>
  MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: true }), {});

const buildDefaultRoles = () => {
  const all = buildAllAccess();
  return [
    {
      id: 'admin',
      name: 'Admin',
      description: 'Full access to all modules.',
      pages: all,
      system: true
    },
    {
      id: 'teacher',
      name: 'Teacher',
      description: 'Students, incidents, inventory, attendance, compliance, messages.',
      pages: {
        dashboard: true,
        students: true,
        incidents: true,
        inventory: true,
        attendance: true,
        compliance: true,
        messages: true,
        'external-messages': true,
        safeguarding: false,
        programmes: false,
        reports: false,
        settings: false,
        'support-workers': false,
        broadcasts: false
      },
      system: true
    },
    {
      id: 'inventory',
      name: 'Inventory',
      description: 'Inventory access only.',
      pages: {
        dashboard: false,
        students: false,
        incidents: false,
        inventory: true,
        attendance: false,
        compliance: false,
        messages: false,
        'external-messages': false,
        safeguarding: false,
        programmes: false,
        reports: false,
        settings: false,
        'support-workers': false,
        broadcasts: false
      },
      system: true
    }
  ];
};

const normalizeRole = (role) => {
  const pages = MODULES.reduce((acc, mod) => {
    acc[mod.id] = Boolean(role.pages?.[mod.id]);
    return acc;
  }, {});
  return {
    id: role.id,
    name: role.name || 'Role',
    description: role.description || '',
    pages,
    system: Boolean(role.system)
  };
};

const readRoles = () => {
  if (typeof window === 'undefined') return buildDefaultRoles();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const defaults = buildDefaultRoles();
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
      return defaults;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) {
      return parsed.map((role) => normalizeRole(role));
    }
    const defaults = buildDefaultRoles();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
    return defaults;
  } catch (error) {
    return buildDefaultRoles();
  }
};

const writeRoles = (roles) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export default class RoleSettingsService {
  list() {
    return readRoles().map((role) => normalizeRole(role));
  }

  getModules() {
    return MODULES;
  }

  getById(id) {
    return this.list().find((role) => role.id === id) || null;
  }

  getPermissions(roleId) {
    const roles = this.list();
    const role = roles.find((entry) => entry.id === roleId) || roles.find((entry) => entry.id === 'teacher');
    return role ? role.pages : buildAllAccess();
  }

  save(roles) {
    writeRoles(roles.map((role) => normalizeRole(role)));
    return this.list();
  }

  add({ name, description = '' }) {
    const roles = this.list();
    const baseId = slugify(name || 'role');
    let id = baseId || `role-${Math.random().toString(36).slice(2, 6)}`;
    let counter = 1;
    const existingIds = new Set(roles.map((role) => role.id));
    while (existingIds.has(id)) {
      counter += 1;
      id = `${baseId}-${counter}`;
    }
    const newRole = normalizeRole({ id, name, description, pages: {} });
    this.save([...roles, newRole]);
    return newRole;
  }

  update(role) {
    const roles = this.list();
    const next = roles.map((entry) => (entry.id === role.id ? normalizeRole({ ...entry, ...role }) : entry));
    this.save(next);
    return next.find((entry) => entry.id === role.id) || null;
  }

  remove(id) {
    const roles = this.list();
    const target = roles.find((role) => role.id === id);
    if (target?.system) return roles;
    const next = roles.filter((role) => role.id !== id);
    this.save(next);
    return next;
  }
}
